import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Search, ChevronRight, Webhook, ArrowRight, Zap, 
  FileSpreadsheet, MessageSquare, Users, Mail, Calendar, 
  Video, Cloud, CreditCard, CheckCircle, Star
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { PublicHeader } from '../components/PublicHeader';
import Footer from '../components/Footer';

// All integration categories (counts will be calculated dynamically)
const CATEGORY_METADATA = [
  { id: 'all', name: 'All Integrations' },
  { id: 'crm', name: 'CRM & Sales' },
  { id: 'storage', name: 'Cloud Storage' },
  { id: 'payment', name: 'Payment' },
  { id: 'email', name: 'Email Marketing' },
  { id: 'automations', name: 'Automations' },
  { id: 'productivity', name: 'Productivity' },
];

// All integrations data
//
// Logo strategy: use Google's public favicon service by default, with curated
// upgrades to higher-fidelity brand SVGs from simple-icons for brands whose
// generic favicon is too small or non-distinct (e.g. Google products that all
// share the "G" favicon). The lucide `icon` stays as automatic fallback if
// the network image fails.
const FAVICON = (domain) =>
  `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
const SI_SVG = (slug) =>
  `https://cdn.jsdelivr.net/npm/simple-icons@latest/icons/${slug}.svg`;

const ALL_INTEGRATIONS = [
  // CRM & Sales
  { id: 'salesforce',     name: 'Salesforce',       description: 'Sync form submissions directly to Salesforce leads',  category: 'crm', icon: MessageSquare, logo: FAVICON('salesforce.com'), featured: true, available: true },
  { id: 'hubspot',        name: 'HubSpot',          description: 'Send leads to HubSpot CRM automatically',             category: 'crm', icon: Users,         logo: FAVICON('hubspot.com'),   featured: true, available: true },
  { id: 'pipedrive',      name: 'Pipedrive',        description: 'Create deals from form submissions',                  category: 'crm', icon: Users,         logo: FAVICON('pipedrive.com'), available: true },
  { id: 'zoho',           name: 'Zoho CRM',         description: 'Connect forms to Zoho CRM modules',                   category: 'crm', icon: Users,         logo: FAVICON('zoho.com'),      available: true },

  // Storage
  { id: 'google_sheets',  name: 'Google Sheets',    description: 'Automatically add responses to spreadsheets',         category: 'storage', icon: FileSpreadsheet, logo: 'https://www.gstatic.com/images/branding/product/2x/sheets_64dp.png',   featured: true, available: true, connected: true },
  { id: 'google_drive',   name: 'Google Drive',     description: 'Store file uploads in Google Drive',                  category: 'storage', icon: Cloud,           logo: 'https://www.gstatic.com/images/branding/product/2x/drive_2020q4_64dp.png',    available: true },
  { id: 'dropbox',        name: 'Dropbox',          description: 'Save form attachments to Dropbox',                    category: 'storage', icon: Cloud,           logo: FAVICON('dropbox.com'),   featured: true, available: true },
  { id: 'onedrive',       name: 'OneDrive',         description: 'Sync files to Microsoft OneDrive',                    category: 'storage', icon: Cloud,           logo: FAVICON('onedrive.live.com'), available: true },
  { id: 'airtable',       name: 'Airtable',         description: 'Send data to Airtable bases',                         category: 'storage', icon: FileSpreadsheet, logo: FAVICON('airtable.com'),  available: true },
  { id: 'notion',         name: 'Notion',           description: 'Create Notion pages from submissions',                category: 'storage', icon: FileSpreadsheet, logo: FAVICON('notion.so'),     available: true },

  // Payment
  { id: 'razorpay',       name: 'Razorpay',         description: 'Accept payments with Razorpay',                       category: 'payment', icon: CreditCard, logo: FAVICON('razorpay.com'), featured: true, available: true, configured: true },
  { id: 'paypal',         name: 'PayPal',           description: 'Process PayPal payments on forms',                    category: 'payment', icon: CreditCard, logo: FAVICON('paypal.com'),   featured: true, available: true },
  { id: 'square',         name: 'Square',           description: 'Accept Square payments',                              category: 'payment', icon: CreditCard, logo: FAVICON('squareup.com'), available: true },

  // Email Marketing
  { id: 'mailchimp',      name: 'Mailchimp',        description: 'Add subscribers to Mailchimp lists',                  category: 'email', icon: Mail, logo: FAVICON('mailchimp.com'),      featured: true, available: true },
  { id: 'sendgrid',       name: 'SendGrid',         description: 'Send transactional emails via SendGrid',              category: 'email', icon: Mail, logo: FAVICON('sendgrid.com'),       available: true },
  { id: 'mailerlite',     name: 'MailerLite',       description: 'Grow your email list with MailerLite',                category: 'email', icon: Mail, logo: FAVICON('mailerlite.com'),     available: true },
  { id: 'convertkit',     name: 'ConvertKit',       description: 'Add subscribers to ConvertKit sequences',             category: 'email', icon: Mail, logo: FAVICON('kit.com'),            available: true },
  { id: 'activecampaign', name: 'ActiveCampaign',   description: 'Trigger automations in ActiveCampaign',               category: 'email', icon: Mail, logo: FAVICON('activecampaign.com'), available: true },

  // Automations (Including Custom Webhook)
  { id: 'custom_webhook', name: 'Custom Webhook',   description: 'Send data to any URL - connect to 5000+ apps via Zapier, Make.com, n8n', category: 'automations', icon: Webhook, featured: true, available: true, isWebhook: true },
  { id: 'zapier',         name: 'Zapier',           description: 'Connect to 6000+ apps via Zapier webhooks',           category: 'automations', icon: Zap, logo: FAVICON('zapier.com'),  featured: true, available: true },
  { id: 'make',           name: 'Make.com',         description: 'Build powerful automations with Make',                category: 'automations', icon: Zap, logo: FAVICON('make.com'),    available: true },
  { id: 'n8n',            name: 'n8n',              description: 'Self-hosted workflow automation',                     category: 'automations', icon: Zap, logo: FAVICON('n8n.io'),      available: true },
  { id: 'pabbly',         name: 'Pabbly Connect',   description: 'Affordable automation platform',                      category: 'automations', icon: Zap, logo: FAVICON('pabbly.com'),  available: true },
  { id: 'ifttt',          name: 'IFTTT',            description: 'Simple automation triggers',                          category: 'automations', icon: Zap, logo: FAVICON('ifttt.com'),   available: true },

  // Productivity
  { id: 'slack',            name: 'Slack',            description: 'Get notifications in Slack channels',               category: 'productivity', icon: MessageSquare, logo: FAVICON('slack.com'),              featured: true, available: true },
  { id: 'microsoft_teams',  name: 'Microsoft Teams',  description: 'Post form submissions to Teams channels',           category: 'productivity', icon: Users,         logo: FAVICON('teams.microsoft.com'),    featured: true, available: true },
  { id: 'discord',          name: 'Discord',          description: 'Send notifications to Discord servers',             category: 'productivity', icon: MessageSquare, logo: FAVICON('discord.com'),            available: true },
  { id: 'google_calendar',  name: 'Google Calendar',  description: 'Create calendar events from bookings',              category: 'productivity', icon: Calendar,      logo: 'https://www.gstatic.com/images/branding/product/2x/calendar_2020q4_64dp.png',          featured: true, available: true },
  { id: 'zoom',             name: 'Zoom',             description: 'Schedule Zoom meetings automatically',              category: 'productivity', icon: Video,         logo: FAVICON('zoom.us'),                available: true },
  { id: 'trello',           name: 'Trello',           description: 'Create Trello cards from submissions',              category: 'productivity', icon: FileSpreadsheet, logo: FAVICON('trello.com'),           available: true },
  { id: 'asana',            name: 'Asana',            description: 'Create Asana tasks from forms',                     category: 'productivity', icon: FileSpreadsheet, logo: FAVICON('asana.com'),            available: true },
  { id: 'monday',           name: 'Monday.com',       description: 'Add items to Monday.com boards',                    category: 'productivity', icon: FileSpreadsheet, logo: FAVICON('monday.com'),           available: true },
];

export const IntegrationsPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Calculate dynamic counts for each category
  const CATEGORIES = CATEGORY_METADATA.map(cat => ({
    ...cat,
    count: cat.id === 'all' 
      ? ALL_INTEGRATIONS.length 
      : ALL_INTEGRATIONS.filter(int => int.category === cat.id).length
  }));

  // Filter integrations
  const filteredIntegrations = ALL_INTEGRATIONS.filter(int => {
    const matchesSearch = int.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         int.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || int.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const featuredIntegrations = ALL_INTEGRATIONS.filter(int => int.featured);

  return (
    <div className="min-h-screen bg-slate-50 overflow-x-hidden">
      {/* Header */}
      <PublicHeader />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-indigo-600 via-indigo-700 to-purple-700 py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
            100+ Integrations
          </h1>
          <p className="text-base sm:text-xl text-indigo-100 mb-6 sm:mb-8 max-w-2xl mx-auto px-4">
            Connect your forms to the tools you already use. Automate workflows, sync data, and supercharge your productivity.
          </p>
          
          {/* Search Bar */}
          <div className="max-w-xl mx-auto relative px-4 sm:px-0">
            <Search className="absolute left-7 sm:left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/80 pointer-events-none z-10" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search integrations..."
              className="w-full pl-12 py-4 sm:py-6 text-base sm:text-lg rounded-xl bg-white/10 backdrop-blur-sm border-2 border-white/40 text-white placeholder:text-white/70 hover:bg-white/15 hover:border-white/60 focus:bg-white/20 focus:border-white focus-visible:ring-2 focus-visible:ring-white/40 focus-visible:ring-offset-0 shadow-xl transition-all"
              data-testid="integration-search"
            />
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 overflow-x-hidden">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <aside className="w-64 flex-shrink-0 hidden lg:block">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sticky top-24">
              <h3 className="font-semibold text-slate-900 mb-4">Categories</h3>
              <nav className="space-y-1">
                {CATEGORIES.map(category => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                      selectedCategory === category.id
                        ? 'bg-indigo-50 text-indigo-700 font-medium'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                    data-testid={`filter-${category.id}`}
                  >
                    <span>{category.name}</span>
                    <span className="text-xs text-slate-400">{category.count}</span>
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1">
            {/* Custom Webhook Banner */}
            <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl p-4 sm:p-6 mb-8 text-white">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-start sm:items-center gap-3 sm:gap-4">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Webhook className="w-6 h-6 sm:w-7 sm:h-7" />
                  </div>
                  <div>
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h3 className="text-lg sm:text-xl font-bold">Custom Webhook Integration</h3>
                      <Badge className="bg-white/20 text-white border-0 text-xs">NEW</Badge>
                    </div>
                    <p className="text-indigo-100 text-sm sm:text-base">
                      Connect to 5000+ apps instantly via Zapier, Make.com, n8n, or your own systems
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={() => navigate(isAuthenticated ? '/dashboard' : '/register')}
                  className="bg-white text-indigo-600 hover:bg-indigo-50 w-full sm:w-auto flex-shrink-0"
                  data-testid="try-webhook-btn"
                >
                  Try Webhooks
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>

            {/* Mobile Category Filter */}
            <div className="lg:hidden mb-6 overflow-x-auto">
              <div className="flex gap-2 pb-2">
                {CATEGORIES.slice(0, 5).map(category => (
                  <Button
                    key={category.id}
                    variant={selectedCategory === category.id ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedCategory(category.id)}
                    className="whitespace-nowrap"
                  >
                    {category.name}
                  </Button>
                ))}
              </div>
            </div>

            {/* Featured Section - Only show when not searching */}
            {!searchQuery && selectedCategory === 'all' && (
              <div className="mb-10">
                <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Star className="w-5 h-5 text-amber-500" />
                  Featured Integrations
                </h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {featuredIntegrations.slice(0, 6).map(integration => (
                    <IntegrationCard key={integration.id} integration={integration} />
                  ))}
                </div>
              </div>
            )}

            {/* All Integrations */}
            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-4">
                {selectedCategory === 'all' ? 'All Integrations' : CATEGORIES.find(c => c.id === selectedCategory)?.name}
                <span className="text-sm font-normal text-slate-500 ml-2">
                  ({filteredIntegrations.length} integrations)
                </span>
              </h2>
              
              {filteredIntegrations.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
                  <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No integrations found</h3>
                  <p className="text-slate-600">Try adjusting your search or filter</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredIntegrations.map(integration => (
                    <IntegrationCard key={integration.id} integration={integration} />
                  ))}
                </div>
              )}
            </div>

            {/* CTA Section */}
            <div className="mt-12 bg-white rounded-2xl border border-slate-200 p-8 text-center">
              <h3 className="text-2xl font-bold text-slate-900 mb-2">
                Don't see your integration?
              </h3>
              <p className="text-slate-600 mb-6">
                Use Custom Webhooks to connect to any app or request a native integration.
              </p>
              <div className="flex justify-center gap-4">
                <Button 
                  onClick={() => navigate(isAuthenticated ? '/dashboard' : '/register')}
                  className="bg-indigo-600 hover:bg-orange-500"
                >
                  Try Custom Webhooks
                </Button>
                <Button variant="outline" onClick={() => navigate('/contact')}>
                  Request Integration
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

// Integration Card Component
const IntegrationCard = ({ integration }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const Icon = integration.icon;
  const [logoFailed, setLogoFailed] = useState(false);
  const showLogo = integration.logo && !logoFailed;

  return (
    <div 
      className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5 hover:shadow-lg hover:border-indigo-200 transition-all cursor-pointer group"
      onClick={() => navigate(isAuthenticated ? '/dashboard' : '/register')}
      data-testid={`integration-card-${integration.id}`}
    >
      <div className="flex items-start gap-3 sm:gap-4">
        <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden ${
          integration.isWebhook 
            ? 'bg-gradient-to-br from-indigo-500 to-purple-500' 
            : 'bg-white ring-1 ring-slate-200 group-hover:ring-indigo-200'
        }`}>
          {integration.isWebhook ? (
            <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          ) : showLogo ? (
            <img
              src={integration.logo}
              alt={`${integration.name} logo`}
              loading="lazy"
              onError={() => setLogoFailed(true)}
              className="w-6 h-6 sm:w-7 sm:h-7 object-contain"
              data-testid={`integration-logo-${integration.id}`}
            />
          ) : (
            <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-slate-600 group-hover:text-indigo-600" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-1">
            <h4 className="font-semibold text-slate-900 group-hover:text-indigo-600 transition-colors text-sm sm:text-base">
              {integration.name}
            </h4>
            {integration.featured && (
              <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px] sm:text-xs px-1.5 py-0">Featured</Badge>
            )}
            {integration.connected && (
              <Badge className="bg-green-100 text-green-700 border-0 text-[10px] sm:text-xs px-1.5 py-0">
                <CheckCircle className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5 sm:mr-1" />
                Connected
              </Badge>
            )}
          </div>
          <p className="text-xs sm:text-sm text-slate-600 line-clamp-2">{integration.description}</p>
        </div>
      </div>
      <div className="mt-3 sm:mt-4 pt-2 sm:pt-3 border-t border-slate-100">
        <span className="text-xs sm:text-sm text-indigo-600 font-medium group-hover:underline flex items-center gap-1">
          Connect
          <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
        </span>
      </div>
    </div>
  );
};

export default IntegrationsPage;
